import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretRight, X } from '@phosphor-icons/react';
import type { FinancePartnerEligibilitySummary } from '../../components/Layout-Components/Editor-Layout/Finance-Partner-Eligibility/FinancePartnerEligibility';
import { resolveVendorLossesKey, vendorLossesValues } from '../../Constants';
import { formatPpaRate, type PreContractPpwMetricsOverride } from '../lib/preContractPpwPricing';
import type { PreContractProductPricingOption } from '../lib/preContractProductPartners';
import {
    lossesSourceLabel,
    usePreContractLossesPricing,
} from '../lib/usePreContractLossesPricing';
import { loadPreContractFullModuleLayouts } from '../lib/preContractFullModuleLayouts';
import { PreContractSelectedModulesPopover } from './PreContractSelectedModulesPopover';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import type { PreContractSummaryItem } from './PreContractModuleStep.types';
import type { SolarDesignRevision } from '../lib/preContractSolarDesign';

export type PreContractSolarLayoutDetailsViewportPreview = {
    label: string;
    layouts: PreContractIntakeForm['roofModuleLayouts'];
    roofSelections: boolean[];
    baseMetrics?: PreContractPpwMetricsOverride | null;
    modified?: boolean;
    /** When false, hide On viewport / Modified chips used for AI option previews. */
    showPreviewFlags?: boolean;
};

export type PreContractSolarLayoutDetailsBarProps = {
    form: PreContractIntakeForm;
    summaryItems: PreContractSummaryItem[];
    financePartner?: string | null;
    financeOptions?: PreContractProductPricingOption[];
    financePartnerEligibility?: FinancePartnerEligibilitySummary[];
    selectedPricingOption?: PreContractProductPricingOption | null;
    onSelectFinanceOption?: (option: PreContractProductPricingOption) => void;
    onFinalize?: () => void | Promise<void>;
    finalizeDisabled?: boolean;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    viewportPreview?: PreContractSolarLayoutDetailsViewportPreview | null;
    designRevision?: SolarDesignRevision | null;
    designModified?: boolean;
    appliedDesignLabel?: string | null;
    utilityPopoverOpen?: boolean;
    utilityComplete?: boolean;
    utilityDisabled?: boolean;
    utilityDisabledReason?: string | null;
    utilityWrapRef?: React.RefObject<HTMLDivElement | null>;
    utilityButtonRef?: React.RefObject<HTMLButtonElement | null>;
    onToggleUtility?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    highlightValidation?: boolean;
};

function formatDetailMoney(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

function formatDetailPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function formatDetailRate(
    option: PreContractProductPricingOption | null | undefined,
    ppaRate: number | null | undefined,
): string {
    if (option?.apr != null && option.apr > 0) return `${option.apr.toFixed(2)}% APR`;
    if (ppaRate != null && ppaRate > 0) return formatPpaRate(ppaRate);
    if (option?.escalator != null && option.escalator > 0) {
        const pct = Math.round(option.escalator * 10000) / 100;
        return `${pct}% esc.`;
    }
    return '—';
}

function resolveDesignTitle(
    designRevision: SolarDesignRevision | null | undefined,
    appliedDesignLabel: string | null | undefined,
    designModified: boolean,
): string {
    const named = appliedDesignLabel?.trim();
    if (named) return named;
    if (designRevision === 'ai') return 'AI design';
    if (designRevision === 'manual') return 'Manual layout';
    if (designModified) return 'Updated layout';
    return 'Current layout';
}

export const PreContractSolarLayoutDetailsBar: React.FC<
    PreContractSolarLayoutDetailsBarProps
> = ({
    form,
    summaryItems,
    financePartner = null,
    financeOptions = [],
    financePartnerEligibility = [],
    selectedPricingOption = null,
    onSelectFinanceOption,
    onFinalize,
    finalizeDisabled = false,
    metricsOverride = null,
    viewportPreview = null,
    designRevision = null,
    designModified = false,
    appliedDesignLabel = null,
    utilityPopoverOpen = false,
    utilityComplete = false,
    utilityDisabled = false,
    utilityDisabledReason = null,
    utilityWrapRef,
    utilityButtonRef,
    onToggleUtility,
    highlightValidation = false,
}) => {
    const [finalizing, setFinalizing] = useState(false);
    const [lossesDetailsOpen, setLossesDetailsOpen] = useState(false);
    const [selectedModulesOpen, setSelectedModulesOpen] = useState(false);
    const {
        lossesVendor,
        expectedLossesVendor,
        lossesMismatch,
        cards,
        layouts: effectiveLayouts,
        roofSelections: effectiveRoofSelections,
        moduleCount,
        systemKw,
        multiplier,
        activeProductionKwh,
        activeProductionRatio,
        selectedProductionEstimate,
        productionLoading,
    } = usePreContractLossesPricing({
        form,
        financePartner,
        financeOptions,
        financePartnerEligibility,
        selectedPricingOption,
        metricsOverride,
        viewportPreview,
        onSelectFinanceOption,
    });

    const viewportPreviewActive = viewportPreview != null;
    const showPreviewChrome = viewportPreview?.showPreviewFlags !== false;

    const metricByLabel = (label: string) =>
        summaryItems.find((item) => item.label === label)?.value ?? '—';

    // The modules on the board are the one source of truth, so this agrees with the
    // losses tabs instead of falling back to the flat-yield summary figure. It is stated
    // under the applied losses, which is not always the vendor the viewer last solved.
    const production = (() => {
        if (productionLoading || selectedProductionEstimate.waitingForShade) return 'Loading';
        if (activeProductionKwh != null && activeProductionKwh > 0) {
            return `${Math.round(activeProductionKwh).toLocaleString()} kWh`;
        }
        return viewportPreviewActive ? '—' : metricByLabel('System Production');
    })();

    const utilityUsage = metricByLabel('Utility Usage');
    const title = viewportPreviewActive
        ? viewportPreview.label
        : resolveDesignTitle(designRevision, appliedDesignLabel, designModified);
    const showViewportFlag = viewportPreviewActive && showPreviewChrome;
    const showModifiedFlag = viewportPreviewActive
        ? Boolean(viewportPreview?.modified) && showPreviewChrome
        : designModified;

    const lossRows = vendorLossesValues[resolveVendorLossesKey(lossesVendor)] ?? [];
    const activeCard = cards.find((card) => card.vendor === lossesVendor) ?? cards[0] ?? null;

    const handleFinalize = async () => {
        if (!onFinalize || finalizeDisabled || finalizing) return;
        setFinalizing(true);
        try {
            await onFinalize();
        } finally {
            setFinalizing(false);
        }
    };

    type DetailMetric = {
        label: string;
        value: string;
        loading?: boolean;
        accent?: boolean;
    };

    const renderMetric = (metric: DetailMetric) => (
        <div key={metric.label} className="pci-solar-layout-details__metric">
            <dt>{metric.label}</dt>
            <dd
                className={
                    [
                        metric.loading ? 'pci-solar-layout-details__metric-value--loading' : '',
                        metric.accent ? 'pci-solar-layout-details__metric-value--accent' : '',
                    ]
                        .filter(Boolean)
                        .join(' ') || undefined
                }
            >
                {metric.value}
            </dd>
        </div>
    );

    const systemMetrics: DetailMetric[] = [
        { label: 'System size', value: systemKw > 0 ? `${systemKw.toFixed(2)} kW` : '—' },
        { label: 'Modules', value: moduleCount > 0 ? String(moduleCount) : '—' },
        { label: 'Production', value: production, loading: production === 'Loading' },
        {
            label: 'Multiplier',
            value: multiplier != null && multiplier > 0 ? multiplier.toFixed(3) : '—',
        },
        { label: 'Utility usage', value: utilityUsage },
        { label: 'PPW', value: formatDetailPpw(activeCard?.quote?.ppw ?? null) },
        { label: 'Rate', value: formatDetailRate(activeCard?.option, activeCard?.quote?.rate) },
        { label: 'EPC', value: formatDetailMoney(activeCard?.quote?.epc ?? null) },
    ];

    return (
        <section
            className={`pci-solar-layout-details${
                viewportPreviewActive && showPreviewChrome
                    ? ' pci-solar-layout-details--viewport-preview'
                    : ''
            }${showModifiedFlag ? ' pci-solar-layout-details--modified' : ''}`}
            aria-label="Design details"
        >
            <header className="pci-solar-layout-details__head">
                <div className="pci-solar-layout-details__chip pci-solar-layout-details__chip--design">
                    <div className="pci-solar-layout-details__chip-main">
                        <p className="pci-solar-layout-details__eyebrow">Design details</p>
                        <div className="pci-solar-layout-details__title-row">
                            <h3 className="pci-solar-layout-details__title">{title}</h3>
                            {showViewportFlag ? (
                                <span className="pci-solar-layout-details__flag pci-solar-layout-details__flag--viewport">
                                    On viewport
                                </span>
                            ) : null}
                            {showModifiedFlag ? (
                                <span className="pci-solar-layout-details__flag pci-solar-layout-details__flag--modified">
                                    Modified
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {onToggleUtility ? (
                        <div ref={utilityWrapRef} className="pci-utility-popover-wrap">
                            <button
                                ref={utilityButtonRef}
                                type="button"
                                className={`pci-unified-nav__btn pci-solar-layout-details__utility-btn${
                                    utilityPopoverOpen ? ' pci-unified-nav__btn--active' : ''
                                }${
                                    utilityComplete
                                        ? ' pci-unified-nav__btn--complete'
                                        : ' pci-unified-nav__btn--needs'
                                }${
                                    highlightValidation && !utilityComplete
                                        ? ' pci-unified-nav__btn--attention'
                                        : ''
                                }`}
                                disabled={utilityDisabled}
                                title={utilityDisabledReason ?? undefined}
                                aria-expanded={utilityPopoverOpen}
                                aria-haspopup="dialog"
                                onClick={(event) => {
                                    if (utilityDisabled) return;
                                    onToggleUtility(event);
                                }}
                            >
                                Utility Usage
                            </button>
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className={`pci-unified-nav__btn pci-solar-layout-details__utility-btn${
                            lossesDetailsOpen ? ' pci-unified-nav__btn--active' : ''
                        }${
                            lossesMismatch
                                ? ' pci-unified-nav__btn--needs pci-unified-nav__btn--attention'
                                : ' pci-unified-nav__btn--complete'
                        }`}
                        aria-haspopup="dialog"
                        aria-expanded={lossesDetailsOpen}
                        title={
                            lossesMismatch && expectedLossesVendor
                                ? `Applied: ${lossesSourceLabel(lossesVendor)} · expected ${lossesSourceLabel(expectedLossesVendor)}`
                                : `Applied: ${lossesSourceLabel(lossesVendor)}`
                        }
                        onClick={() => setLossesDetailsOpen(true)}
                    >
                        Losses · {lossesVendor}
                    </button>
                    {onFinalize ? (
                        <button
                            type="button"
                            className="pci-solar-layout-details__finalize"
                            disabled={finalizeDisabled || finalizing}
                            onClick={() => void handleFinalize()}
                        >
                            {finalizing ? 'Opening…' : 'Finalize'}
                            <CaretRight size={13} weight="bold" aria-hidden />
                        </button>
                    ) : null}
                </div>
            </header>

            <div className="pci-solar-layout-details__groups">
                <section className="pci-solar-layout-details__group pci-solar-layout-details__group--system">
                    <header className="pci-solar-layout-details__group-head">
                        <h4 className="pci-solar-layout-details__group-title">System</h4>
                        <PreContractSelectedModulesPopover
                            layouts={effectiveLayouts}
                            allModulesLayouts={
                                loadPreContractFullModuleLayouts() ?? undefined
                            }
                            roofSelections={effectiveRoofSelections}
                            escalator={form.targetEscalator}
                            rate={form.targetMinPpaRate}
                            form={form}
                            actualProductionKwh={activeProductionKwh ?? undefined}
                            productionScale={activeProductionRatio}
                            estimateMissingProduction
                            systemPpw={activeCard?.quote?.ppw ?? null}
                            oneDrawSystemPpw={
                                cards.find((card) => card.vendor === 'oneDRAW')?.quote?.ppw ?? null
                            }
                            enfinSystemPpw={
                                cards.find((card) => card.vendor === 'EnFin')?.quote?.ppw ?? null
                            }
                            open={selectedModulesOpen}
                            onOpenChange={setSelectedModulesOpen}
                            contextLabel={title}
                            variant="toolbar"
                            compact
                        />
                    </header>
                    <dl className="pci-solar-layout-details__metrics pci-solar-layout-details__metrics--system">
                        {systemMetrics.map(renderMetric)}
                    </dl>
                </section>
            </div>
            {lossesDetailsOpen
                ? createPortal(
                      <div
                          className="pci-selected-design__losses-overlay"
                          role="presentation"
                          onClick={() => setLossesDetailsOpen(false)}
                      >
                          <div
                              className="pci-selected-design__losses-dialog"
                              role="dialog"
                              aria-modal="true"
                              aria-label={`${lossesVendor} losses details`}
                              onClick={(event) => event.stopPropagation()}
                          >
                              <header className="pci-selected-design__losses-dialog-head">
                                  <div>
                                      <p className="pci-board-v2-label">Losses</p>
                                      <h3>{lossesVendor} breakdown</h3>
                                  </div>
                                  <button
                                      type="button"
                                      className="pci-selected-design__losses-dialog-close"
                                      aria-label="Close losses details"
                                      onClick={() => setLossesDetailsOpen(false)}
                                  >
                                      <X size={16} weight="bold" />
                                  </button>
                              </header>
                              <table className="pci-selected-design__losses-table">
                                  <thead>
                                      <tr>
                                          <th scope="col">Losses</th>
                                          <th scope="col">%</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {lossRows.map((row) => (
                                          <tr key={row.lossName}>
                                              <td>{row.lossName}</td>
                                              <td>{row.percentage}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </div>,
                      document.body,
                  )
                : null}
        </section>
    );
};

export default PreContractSolarLayoutDetailsBar;
